import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Settings, 
  Globe, 
  Palette, 
  Bell, 
  Shield,
  Eye,
  EyeOff,
  Save,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { authService } from '../../services/auth'
import type { User } from '../../services/auth'

const UserSettings: React.FC = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const [preferences, setPreferences] = useState({
    theme: 'auto' as 'light' | 'dark' | 'auto',
    language: 'zh-CN',
    notifications: {
      email: true,
      push: true,
      sms: false,
      marketing: true
    },
    privacy: {
      profileVisible: true,
      activityVisible: false,
      dataSharing: true
    }
  })

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = authService.getCurrentUserLocal()
        if (currentUser) {
          setUser(currentUser)
          setPreferences(currentUser.preferences)
        } else {
          const userData = await authService.getCurrentUser()
          setUser(userData)
          setPreferences(userData.preferences)
        }
      } catch (error) {
        console.error('加载用户设置失败:', error)
        setError('加载用户设置失败')
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [])

  const handlePreferenceChange = (path: string, value: any) => {
    setPreferences(prev => {
      const newPrefs = { ...prev }
      const keys = path.split('.')
      let current = newPrefs
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]]
      }
      
      current[keys[keys.length - 1]] = value
      return newPrefs
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setMessage('')

    try {
      const updatedUser = await authService.updateUser({ preferences })
      setUser(updatedUser)
      setMessage('设置保存成功')
    } catch (error: any) {
      setError(error.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>账户设置 - DeepLink用户中心</title>
        <meta name="description" content="管理您的账户设置和偏好" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/user')}
                className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                返回用户中心
              </button>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">账户设置</h1>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Messages */}
            {message && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center"
              >
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <span className="text-green-700 dark:text-green-400">{message}</span>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center"
              >
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-700 dark:text-red-400">{error}</span>
              </motion.div>
            )}

            <div className="space-y-6">
              {/* Appearance Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center">
                    <Palette className="w-5 h-5 text-blue-500 mr-3" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">外观设置</h3>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      主题模式
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { value: 'light', label: '浅色模式', icon: '☀️' },
                        { value: 'dark', label: '深色模式', icon: '🌙' },
                        { value: 'auto', label: '跟随系统', icon: '🖥️' }
                      ].map((theme) => (
                        <button
                          key={theme.value}
                          onClick={() => handlePreferenceChange('theme', theme.value)}
                          className={`p-4 rounded-lg border-2 transition-colors ${
                            preferences.theme === theme.value
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <div className="text-2xl mb-2">{theme.icon}</div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {theme.label}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      语言设置
                    </label>
                    <select
                      value={preferences.language}
                      onChange={(e) => handlePreferenceChange('language', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="zh-CN">简体中文</option>
                      <option value="en-US">English</option>
                      <option value="ja-JP">日本語</option>
                      <option value="ko-KR">한국어</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center">
                    <Bell className="w-5 h-5 text-green-500 mr-3" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">通知设置</h3>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {[
                    { key: 'email', label: '邮件通知', description: '接收重要更新和账户相关邮件' },
                    { key: 'push', label: '推送通知', description: '接收应用内推送通知' },
                    { key: 'sms', label: '短信通知', description: '接收短信提醒（可能产生费用）' },
                    { key: 'marketing', label: '营销通知', description: '接收产品更新和促销信息' }
                  ].map((notification) => (
                    <div key={notification.key} className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {notification.label}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {notification.description}
                        </div>
                      </div>
                      <button
                        onClick={() => handlePreferenceChange(`notifications.${notification.key}`, !preferences.notifications[notification.key])}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences.notifications[notification.key]
                            ? 'bg-blue-600'
                            : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences.notifications[notification.key] ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Privacy Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 text-purple-500 mr-3" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">隐私设置</h3>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {[
                    { key: 'profileVisible', label: '公开个人资料', description: '允许其他用户查看您的基本信息' },
                    { key: 'activityVisible', label: '公开活动记录', description: '允许其他用户查看您的活动记录' },
                    { key: 'dataSharing', label: '数据共享', description: '允许我们使用您的数据改进服务' }
                  ].map((privacy) => (
                    <div key={privacy.key} className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {privacy.label}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {privacy.description}
                        </div>
                      </div>
                      <button
                        onClick={() => handlePreferenceChange(`privacy.${privacy.key}`, !preferences.privacy[privacy.key])}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences.privacy[privacy.key]
                            ? 'bg-blue-600'
                            : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences.privacy[privacy.key] ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? (
                    <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  保存设置
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default UserSettings